/* Play media only while visible; keep motion preferences and fallback access. */
(() => {
  class CaliveyorCarousel extends HTMLElement {
    connectedCallback() {
      if (!this.rail) {
        this.rail = this.querySelector('.cv-family-nav');
        this.controls = this.querySelector('.cv-carousel-controls');
        this.pauseButton = this.querySelector('[data-carousel-pause]');
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.userPaused = this.dataset.auto !== 'true';
        this.visible = false;
        this.querySelector('[data-carousel-prev]').addEventListener('click', () => this.move(-1));
        this.querySelector('[data-carousel-next]').addEventListener('click', () => this.move(1));
        this.pauseButton.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
          this.pauseButton.setAttribute('aria-label', this.userPaused ? 'Resume product carousel' : 'Pause product carousel');
          this.pauseButton.firstElementChild.textContent = this.userPaused ? '▷' : 'Ⅱ';
          if (!this.userPaused) { this.hovered = false; this.focused = false; }
          this.update();
        });
        this.addEventListener('pointerenter', () => { this.hovered = true; this.update(); });
        this.addEventListener('pointerleave', () => { this.hovered = false; this.update(); });
        this.rail.addEventListener('pointerenter', () => { this.hovered = true; this.update(); });
        this.addEventListener('focusin', () => { this.focused = true; this.update(); });
        this.addEventListener('focusout', e => { this.focused = this.contains(e.relatedTarget); this.update(); });
        this.rail.addEventListener('touchstart', () => { this.hovered = true; this.update(); }, { passive: true });
        for (const event of ['touchend', 'touchcancel']) this.rail.addEventListener(event, () => { this.hovered = false; this.update(); }, { passive: true });
        this.onVisibility = () => this.update();
        this.onMotion = () => this.update();
        this.resize = new ResizeObserver(() => this.update());
        this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.update(); });
      }
      document.addEventListener('visibilitychange', this.onVisibility);
      this.motion.addEventListener('change', this.onMotion);
      this.resize.observe(this.rail);
      this.observer.observe(this);
      this.update();
    }
    move(direction) {
      const cards = Array.from(this.rail.querySelectorAll('.cv-family-nav__item'));
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      if (!cards.length || max <= 2) return;
      const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth;
      let target = this.rail.scrollLeft + direction * step;
      if (direction > 0 && this.rail.scrollLeft >= max - 2) target = 0;
      if (direction < 0 && this.rail.scrollLeft <= 2) target = max;
      this.rail.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: this.motion.matches ? 'auto' : 'smooth' });
    }
    update() {
      clearInterval(this.timer);
      const overflow = this.rail.scrollWidth > this.rail.clientWidth + 2;
      this.controls.hidden = !overflow;
      this.pauseButton.hidden = this.dataset.auto !== 'true' || this.motion.matches;
      if (!this.isConnected || !overflow || !this.visible || this.userPaused || this.hovered || this.focused || document.hidden || this.motion.matches) return;
      this.timer = setInterval(() => this.move(1), Number(this.dataset.interval) || 7000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.motion?.removeEventListener('change', this.onMotion);
      this.resize?.disconnect();
      this.observer?.disconnect();
    }
  }

  class CaliveyorModel extends HTMLElement {
    connectedCallback() {
      if (!this.viewer) {
        this.viewer = this.querySelector('model-viewer');
        this.status = this.querySelector('[data-model-status]');
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.userPaused = this.motion.matches;
        this.visible = false;
        // Stop turning as soon as a customer chooses an angle.
        this.viewer.addEventListener('pointerdown', () => { this.userPaused = true; this.update(); });
        this.viewer.addEventListener('keydown', e => {
          if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) { this.userPaused = true; this.update(); }
        });
        this.viewer.addEventListener('load', () => {
          this.dataset.loaded = 'true';
          this.status.textContent = 'Drag to explore in 3D';
          this.update();
        });
        this.viewer.addEventListener('error', () => {
          delete this.dataset.loaded;
          this.status.textContent = 'Product preview · 3D view unavailable';
        });
        this.onVisibility = () => this.update();
        this.onMotion = () => { if (this.motion.matches) this.userPaused = true; this.update(); };
        this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.update(); }, { threshold: .1 });
        this.loadViewer = () => {
          if (!customElements.get('model-viewer') && window.Shopify?.loadFeatures) {
            // Share Shopify's own viewer with the existing native product galleries.
            Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: errors => {
              if (errors) this.status.textContent = 'Product preview · 3D view unavailable';
            } }]);
          }
        };
        this.loadViewer();
      }
      window.addEventListener('load', this.loadViewer, { once: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.motion.addEventListener('change', this.onMotion);
      this.observer.observe(this);
      this.update();
    }
    update() {
      const rotating = this.visible && !document.hidden && !this.userPaused && this.dataset.loaded === 'true';
      this.viewer.toggleAttribute('auto-rotate', rotating);
    }
    disconnectedCallback() {
      this.viewer?.removeAttribute('auto-rotate');
      window.removeEventListener('load', this.loadViewer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.motion?.removeEventListener('change', this.onMotion);
      this.observer?.disconnect();
    }
  }

  class CaliveyorRender extends HTMLElement {
    connectedCallback() {
      if (!this.video) {
        this.video = this.querySelector('video');
        this.video.muted = true;
        this.visible = false;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.video.addEventListener('loadeddata', () => { this.dataset.ready = 'true'; });
        this.video.addEventListener('error', () => {
          this.failed = true;
          delete this.dataset.ready;
          this.pause();
        });
        this.onVisibility = () => this.update();
        this.onMotion = () => this.update();
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .25;
          this.update();
        }, { threshold: .25 });
      }
      document.addEventListener('visibilitychange', this.onVisibility);
      this.motion.addEventListener('change', this.onMotion);
      this.observer.observe(this.video);
      this.update();
    }
    // Visitors cannot pause these passive loops, so a pause the browser makes on its
    // own while the page is showing must not stop later playback.
    shouldPlay() {
      return this.isConnected && this.visible && !document.hidden && !this.failed && !this.motion.matches;
    }
    pause() {
      if (!this.video.paused) this.video.pause();
    }
    update() {
      if (!this.shouldPlay()) this.pause();
      else {
        this.video.preload = 'metadata';
        this.video.play().then(() => { if (!this.shouldPlay()) this.pause(); }).catch(() => {});
      }
    }
    disconnectedCallback() {
      if (this.video) this.pause();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.motion?.removeEventListener('change', this.onMotion);
      this.observer?.disconnect();
    }
  }

  class CaliveyorDemos extends HTMLElement {
    connectedCallback() {
      if (!this.tabs) {
        this.tabs = Array.from(this.querySelectorAll('[data-demo-tab]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.visibleVideos = new WeakMap();
        this.fallbacks = new WeakMap();
        this.unavailableVideos = new WeakSet();
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index));
          tab.addEventListener('keydown', event => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = this.tabs.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            this.select(next);
            this.tabs[next].focus();
          });
        });
        this.panels.forEach(panel => {
          const video = panel.querySelector('video');
          video.muted = true;
          this.fallbacks.set(video, panel.querySelector('[data-demo-fallback]'));
          video.addEventListener('play', () => {
            this.unavailableVideos.delete(video);
            this.showFallback(video, false);
          });
          video.addEventListener('error', () => {
            this.unavailableVideos.add(video);
            this.showFallback(video, true);
          });
        });
        this.onVisibility = () => this.update();
        this.onMotion = () => this.update();
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => this.visibleVideos.set(entry.target, entry.isIntersecting && entry.intersectionRatio >= .25));
          this.update();
        }, { threshold: .25 });
      }
      document.addEventListener('visibilitychange', this.onVisibility);
      this.motion.addEventListener('change', this.onMotion);
      this.panels.forEach(panel => this.observer.observe(panel.querySelector('video')));
      this.update();
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
      this.update();
    }
    pause(video) {
      if (!video.paused) video.pause();
    }
    showFallback(video, visible) {
      const link = this.fallbacks.get(video);
      if (link) link.hidden = !visible;
    }
    update() {
      this.panels.forEach(panel => {
        const video = panel.querySelector('video');
        this.showFallback(video, this.motion.matches || this.unavailableVideos.has(video));
        // Like the rotations, these clips cannot be paused by visitors; only this
        // component's own visibility rules stop them.
        if (panel.hidden || !this.visibleVideos.get(video) || document.hidden || this.motion.matches) this.pause(video);
        else {
          video.preload = 'metadata';
          video.play().catch(error => {
            // A tab change or scroll that pauses a starting clip is not a playback failure.
            if (error?.name === 'AbortError') return;
            this.unavailableVideos.add(video);
            this.showFallback(video, true);
          });
        }
      });
    }
    disconnectedCallback() {
      this.panels?.forEach(panel => this.pause(panel.querySelector('video')));
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.motion?.removeEventListener('change', this.onMotion);
      this.observer?.disconnect();
    }
  }

  if (!customElements.get('caliveyor-carousel')) customElements.define('caliveyor-carousel', CaliveyorCarousel);
  if (!customElements.get('caliveyor-model')) customElements.define('caliveyor-model', CaliveyorModel);
  if (!customElements.get('caliveyor-render')) customElements.define('caliveyor-render', CaliveyorRender);
  if (!customElements.get('caliveyor-demos')) customElements.define('caliveyor-demos', CaliveyorDemos);
})();
